import { useEffect } from 'react';
import { PERSONAL_INFO, SOCIAL_LINKS, EDUCATION } from '../lib/constants/personal';
import { DEFAULT_OG_IMAGE_URL } from '../lib/constants/urls';
import { SEO_DEFAULT_KEYWORDS, SEO_KNOWS_ABOUT } from '../lib/constants/content';
import type { SEOMetadata } from '../lib/types';

/**
 * SEO Component
 * Manages meta tags, Open Graph, Twitter Card, and structured data
 */
export function SEO({
  title = 'Welcome',
  description = `${PERSONAL_INFO.bio.short} Based in ${PERSONAL_INFO.location}. Available for freelance projects and full-time opportunities.`,
  keywords = SEO_DEFAULT_KEYWORDS,
  author = PERSONAL_INFO.name,
  ogTitle = `${PERSONAL_INFO.name} - ${PERSONAL_INFO.title}`,
  ogDescription = `AI Engineer with ${EDUCATION.degree} (${EDUCATION.classification}). Expert in LLMs, NLP, Computer Vision, Azure & Microsoft 365. Based in ${PERSONAL_INFO.location}, available for freelance & full-time opportunities.`,
  ogImage = DEFAULT_OG_IMAGE_URL,
  twitterCard = 'summary_large_image',
  robots = 'index, follow',
  type = 'website',
  canonicalPath,
  publishedTime,
}: SEOMetadata = {}) {
  useEffect(() => {
    // Canonical never carries query strings or hashes (tracking params, filters).
    const canonicalUrl = `${window.location.origin}${canonicalPath ?? window.location.pathname}`;

    // Set page title
    document.title = title;

    // Helper function to set or update meta tags
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.content = content;
    };

    // Standard meta tags
    setMetaTag('description', description);
    setMetaTag('keywords', keywords);
    setMetaTag('author', author);
    setMetaTag('robots', robots);
    setMetaTag('language', 'English');
    setMetaTag('revisit-after', '7 days');

    // Open Graph meta tags
    setMetaTag('og:title', ogTitle, true);
    setMetaTag('og:description', ogDescription, true);
    setMetaTag('og:image', ogImage, true);
    setMetaTag('og:type', type, true);
    setMetaTag('og:url', canonicalUrl, true);
    setMetaTag('og:site_name', `${PERSONAL_INFO.name} Portfolio`, true);
    setMetaTag('og:locale', 'en_IE', true);

    // Twitter Card meta tags
    setMetaTag('twitter:card', twitterCard);
    setMetaTag('twitter:title', ogTitle);
    setMetaTag('twitter:description', ogDescription);
    setMetaTag('twitter:image', ogImage);
    setMetaTag('twitter:creator', PERSONAL_INFO.twitterHandle);

    const publishedMeta = document.querySelector('meta[property="article:published_time"]');
    if (type === 'article' && publishedTime) {
      setMetaTag('article:published_time', publishedTime, true);
    } else {
      publishedMeta?.remove();
    }

    // Additional SEO tags (theme colours live in index.html)
    setMetaTag('mobile-web-app-capable', 'yes');
    setMetaTag('apple-mobile-web-app-status-bar-style', 'black-translucent');
    setMetaTag('format-detection', 'telephone=no');

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    // JSON-LD structured data for better SEO
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      // Same @id as the server-rendered graph (lib/seo/siteMeta.js) so the two merge.
      '@id': `${window.location.origin}/#person`,
      name: PERSONAL_INFO.name,
      jobTitle: PERSONAL_INFO.title,
      description: description,
      url: `${window.location.origin}/`,
      email: PERSONAL_INFO.email,
      telephone: PERSONAL_INFO.phone,
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Dublin',
        addressCountry: 'Ireland',
      },
      alumniOf: {
        '@type': 'CollegeOrUniversity',
        name: EDUCATION.institution,
      },
      knowsAbout: [...SEO_KNOWS_ABOUT],
      sameAs: [
        SOCIAL_LINKS.github.url,
        SOCIAL_LINKS.linkedin.url,
      ],
    };

    let scriptTag = document.querySelector('script#seo-json-ld');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.id = 'seo-json-ld';
      scriptTag.type = 'application/ld+json';
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(structuredData);
  }, [title, description, keywords, author, ogTitle, ogDescription, ogImage, twitterCard, robots, type, canonicalPath, publishedTime]);

  return null;
}
